import type { RankedRecovery } from './MatchmakingContracts';
import { t } from '../i18n';
import { ContextualMenu } from '../ui/ContextualMenu';

export interface RankedRecoveryActions {
  onAccept(): Promise<void>;
  onDecline(): Promise<void>;
  revalidate(): Promise<RankedRecovery | null>;
}

export class RankedRecoveryPrompt {
  readonly #menu: ContextualMenu;
  readonly #actions: RankedRecoveryActions;
  readonly #matchId: string;
  #disposed = false;
  #refreshing = false;

  constructor(host: HTMLElement, recovery: RankedRecovery, actions: RankedRecoveryActions) {
    this.#actions = actions;
    this.#matchId = recovery.matchId;
    this.#menu = new ContextualMenu(host, () => undefined);
    this.#menu.show({
      title: t('online:recovery.title'),
      description: t('online:recovery.description', {
        format: recovery.playlist.format,
        team: t(recovery.team === 'blue' ? 'common:teams.blue' : 'common:teams.orange'),
      }),
      initialActionId: 'accept',
      actions: [
        { id: 'accept', label: t('online:recovery.accept'), primary: true, run: actions.onAccept },
        { id: 'decline', label: t('online:recovery.decline'), destructive: true, run: actions.onDecline },
      ],
    });
    window.addEventListener('focus', this.#revalidate);
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    window.removeEventListener('focus', this.#revalidate);
    this.#menu.dispose();
  }

  #revalidate = (): void => {
    if (this.#disposed || this.#refreshing) return;
    this.#refreshing = true;
    void this.#actions.revalidate().then(recovery => {
      if (!this.#disposed && (!recovery || recovery.matchId !== this.#matchId)) this.dispose();
    }).catch(() => undefined).finally(() => { this.#refreshing = false; });
  };
}
